import { jsPDF } from 'jspdf';
import { getDrivingSummary, formatDriveTime, calculateETA } from './tachograph';

function fmtDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}min`;
}

function fmtMin(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function fuelConsumptionDefault(fuelType, vehicleType) {
  const defaults = { diesel: 25, gasoline: 20, electric: 80, lpg: 22, cng: 20, hvo: 25 };
  const base = defaults[fuelType] || 25;
  return vehicleType === 'car' ? Math.round(base * 0.38) : base;
}

// Check if we need a new page
function checkPage(doc, y, needed = 20) {
  if (y + needed > 280) {
    doc.addPage();
    return 20;
  }
  return y;
}

// Draw a horizontal line
function drawLine(doc, y, margin, pageW) {
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, pageW - margin, y);
}

// Section header
function sectionHeader(doc, y, title, margin) {
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 64, 175); // blue-800
  doc.text(title, margin, y);
  return y + 6;
}

/**
 * Export PDF for driver (simplified — no costs)
 */
export function exportDriverPdf(route, vehiclePlate, driverName, extra = {}) {
  return exportRoutePdf(route, vehiclePlate, driverName, { ...extra, includeCosts: false, title: 'PLANIFICAÇÃO DE ROTA — MOTORISTA' });
}

/**
 * Generate the driver PDF as a blob URL (for preview in an iframe).
 * Returns { url, filename } — caller must URL.revokeObjectURL(url) when done.
 */
export function generateDriverPdfBlob(route, vehiclePlate, driverName, extra = {}) {
  return generateRoutePdfBlob(route, vehiclePlate, driverName, { ...extra, includeCosts: false, title: 'PLANIFICAÇÃO DE ROTA — MOTORISTA' });
}

/**
 * Export full route PDF
 * @param {Object} options - { includeCosts: true, title: 'PLANIFICAÇÃO DE ROTA' }
 */
export function exportRoutePdf(route, vehiclePlate, driverName, options = {}) {
  const { includeCosts = true, title = 'PLANIFICAÇÃO DE ROTA' } = options;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const cr = route.computedRoute;
  const distKm = cr ? (cr.distance / 1000) * (route.roundTrip ? 2 : 1) : 0;
  const durationSec = cr ? cr.duration * (route.roundTrip ? 2 : 1) : 0;
  const durationMin = Math.round(durationSec / 60);
  const fuelType = route.fuelType || 'diesel';
  const vehicleType = route.routeOptions?.vehicleType || 'hgv';
  const consumption = route.fuelConsumption ?? fuelConsumptionDefault(fuelType, vehicleType);
  const totalFuel = distKm ? (distKm / 100) * consumption : 0;
  const pricePerUnit = route.fuelPrice ?? (fuelType === 'diesel' ? 1.65 : fuelType === 'electric' ? 0.18 : 1.75);
  const totalFuelCost = totalFuel * pricePerUnit;
  const stops = route.stops || [];
  const waypoints = route.waypoints || [];
  const opts = route.routeOptions || {};
  // Observações (notes) por OT — mapa otId→texto passado pelo chamador; com
  // fallback ao próprio ponto (wp.notes). Limpa o sufixo interno "· Cliente: X".
  const otNotesById = options.otNotesById || {};
  const cleanNote = (s) => String(s || '').replace(/\s*·?\s*Cliente:.*$/i, '').trim();
  const noteFor = (p) => cleanNote(p?.notes ?? (p?.otId != null ? otNotesById[String(p.otId)] : ''));
  const drawNote = (txt, yy) => {
    doc.setFontSize(6.8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(180, 83, 9);
    const lines = doc.splitTextToSize(`Obs.: ${txt}`, contentW - 16);
    doc.text(lines, margin + 8, yy);
    return (lines.length - 1) * 3.4;
  };

  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = 0;

  // ═══════════════════════════════════════════════════════════
  // HEADER BAND
  // ═══════════════════════════════════════════════════════════
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 24, 'F');
  // Blue accent line
  doc.setFillColor(59, 130, 246);
  doc.rect(0, 24, pageW, 1.5, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 11);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text('EcooGPS Planner', margin, 18);
  doc.text(fmtDate(new Date().toISOString()), pageW - margin, 11, { align: 'right' });
  doc.setFontSize(7);
  doc.text(`ID: ${route.dbId || '—'}`, pageW - margin, 18, { align: 'right' });
  y = 32;

  // ═══════════════════════════════════════════════════════════
  // ROUTE NAME + CARGO REF
  // ═══════════════════════════════════════════════════════════
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(route.name || 'Rota sem nome', margin, y);
  y += 5;
  if (route.cargoRef) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Referência de carga: ${route.cargoRef}`, margin, y);
    y += 4;
  }
  y += 4;

  // ═══════════════════════════════════════════════════════════
  // ORIGIN → DESTINATION
  // ═══════════════════════════════════════════════════════════
  doc.setFillColor(240, 253, 244); // green-50
  doc.roundedRect(margin, y, contentW / 2 - 2, 16, 2, 2, 'F');
  doc.setFillColor(254, 242, 242); // red-50
  doc.roundedRect(margin + contentW / 2 + 2, y, contentW / 2 - 2, 16, 2, 2, 'F');

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(34, 197, 94);
  doc.text('● ORIGEM', margin + 3, y + 5);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(route.origin?.name || '—', margin + 3, y + 11, { maxWidth: contentW / 2 - 8 });

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(239, 68, 68);
  doc.text('● DESTINO', margin + contentW / 2 + 5, y + 5);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(route.destination?.name || '—', margin + contentW / 2 + 5, y + 11, { maxWidth: contentW / 2 - 8 });
  y += 22;

  // ═══════════════════════════════════════════════════════════
  // METRICS GRID (6 boxes)
  // ═══════════════════════════════════════════════════════════
  const wpStopMin = waypoints.reduce((s, wp) => s + (wp.stopDuration || 0), 0);
  const tachoStopMin = stops.reduce((s, st) => s + (st.duration || 0), 0);
  const etaResult = route.departureTime ? calculateETA(route.departureTime, durationSec, stops, waypoints.filter(w => w.stopDuration > 0).map(w => ({ duration: w.stopDuration }))) : null;
  const co2Kg = totalFuel * (fuelType === 'diesel' ? 2.64 : fuelType === 'gasoline' ? 2.31 : fuelType === 'hvo' ? 0.4 : 0);

  const metricsRow1 = [
    { label: 'DISTÂNCIA', value: distKm ? `${distKm.toFixed(1)} km` : '—' },
    { label: 'CONDUÇÃO', value: cr ? fmtDuration(durationSec) : '—' },
    { label: 'PARAGENS', value: `${stops.length + waypoints.filter(w => w.stopDuration > 0).length} (${fmtMin(tachoStopMin + wpStopMin)})` },
  ];
  const metricsRow2 = includeCosts
    ? [
        { label: 'COMBUSTÍVEL', value: totalFuel ? `${totalFuel.toFixed(1)} L (${fuelType})` : '—' },
        { label: 'CUSTO ESTIMADO', value: totalFuelCost ? `€${totalFuelCost.toFixed(2)}` : '—' },
        { label: 'PREVISTO CHEGADA', value: etaResult ? fmtDateShort(etaResult.eta.toISOString()) : '—' },
      ]
    : [
        { label: 'PREVISTO CHEGADA', value: etaResult ? fmtDateShort(etaResult.eta.toISOString()) : '—' },
        { label: 'DIAS VIAGEM', value: stops.filter(s => s.type && s.type.includes('rest')).length + 1 + '' },
        { label: 'CO₂', value: co2Kg > 0 ? `${co2Kg.toFixed(1)} kg` : '—' },
      ];

  [metricsRow1, metricsRow2].forEach(row => {
    const boxW = (contentW - 6) / 3;
    row.forEach((m, i) => {
      const x = margin + i * (boxW + 3);
      doc.setFillColor(239, 246, 255);
      doc.roundedRect(x, y, boxW, 16, 2, 2, 'F');
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(m.label, x + 3, y + 5);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(m.value, x + 3, y + 12);
    });
    y += 20;
  });

  // ═══════════════════════════════════════════════════════════
  // MISSION DETAILS
  // ═══════════════════════════════════════════════════════════
  y = sectionHeader(doc, y, 'DETALHES DA MISSÃO', margin);
  const details = [
    vehiclePlate && { label: 'Veículo', value: vehiclePlate },
    driverName && { label: 'Motorista', value: driverName },
    route.departureTime && { label: 'Partida prevista', value: fmtDate(route.departureTime) },
    etaResult && { label: 'Chegada prevista', value: fmtDateShort(etaResult.eta.toISOString()) },
    vehicleType && { label: 'Tipo veículo', value: vehicleType === 'car' ? 'Ligeiro' : vehicleType === 'hgv' ? 'Pesado (HGV)' : vehicleType },
    route.roundTrip && { label: 'Tipo percurso', value: 'Ida e Volta' },
    includeCosts && consumption && { label: 'Consumo médio', value: `${consumption} ${fuelType === 'electric' ? 'kWh' : 'L'}/100km` },
    includeCosts && co2Kg > 0 && { label: 'Emissões CO₂', value: `${co2Kg.toFixed(1)} kg` },
  ].filter(Boolean);

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, contentW, details.length * 5.5 + 6, 2, 2, 'F');
  y += 4;
  details.forEach(d => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(d.label, margin + 4, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
    doc.text(d.value, margin + 44, y);
    y += 5.5;
  });
  y += 6;

  // ═══════════════════════════════════════════════════════════
  // WAYPOINTS (intermediate stops with duration)
  // ═══════════════════════════════════════════════════════════
  if (waypoints.length > 0) {
    y = checkPage(doc, y, 20);
    y = sectionHeader(doc, y, `PERCURSO DETALHADO (${waypoints.length} ponto(s) intermédio(s))`, margin);

    // Origin
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94);
    doc.text('●', margin + 2, y);
    doc.setTextColor(15, 23, 42);
    doc.text(`Partida: ${route.origin?.name || '—'}`, margin + 8, y);
    if (route.departureTime) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(fmtDateShort(route.departureTime), pageW - margin, y, { align: 'right' });
    }
    y += 6;

    waypoints.forEach((wp, i) => {
      y = checkPage(doc, y, 8);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(245, 158, 11);
      doc.text(`${i + 1}`, margin + 2, y);
      doc.setTextColor(15, 23, 42);
      doc.text(wp.name || `Coordenadas: ${wp.coordinates?.[1]?.toFixed(4)}, ${wp.coordinates?.[0]?.toFixed(4)}`, margin + 8, y, { maxWidth: contentW - 50 });
      if (wp.stopDuration > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(59, 130, 246);
        doc.text(`Paragem: ${fmtMin(wp.stopDuration)}`, pageW - margin, y, { align: 'right' });
      }
      const wpObs = noteFor(wp);
      if (wpObs) { y += 4.5; y += drawNote(wpObs, y); }
      y += 6;
    });

    // Destination
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(239, 68, 68);
    doc.text('●', margin + 2, y);
    doc.setTextColor(15, 23, 42);
    doc.text(`Chegada: ${route.destination?.name || '—'}`, margin + 8, y);
    if (etaResult) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(fmtDateShort(etaResult.eta.toISOString()), pageW - margin, y, { align: 'right' });
    }
    const destObs = noteFor(route.destination);
    if (destObs) { y += 4.5; y += drawNote(destObs, y); }
    y += 8;
  }

  // ═══════════════════════════════════════════════════════════
  // TACHOGRAPH — DRIVING PLAN (EC 561/2006)
  // ═══════════════════════════════════════════════════════════
  if (stops.length > 0) {
    y = checkPage(doc, y, 30);
    y = sectionHeader(doc, y, 'PLANO DE CONDUÇÃO — Reg. (CE) n.º 561/2006', margin);

    // Summary badges
    const summary = getDrivingSummary(stops, durationMin);
    doc.setFillColor(239, 246, 255);
    doc.roundedRect(margin, y, contentW, 12, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text(`Condução: ${summary.totalDriveFormatted}`, margin + 4, y + 5);
    doc.setTextColor(245, 158, 11);
    doc.text(`${summary.breaks} pausa(s)`, margin + 48, y + 5);
    doc.setTextColor(139, 92, 246);
    doc.text(`${summary.dailyRests} descanso(s)`, margin + 76, y + 5);
    doc.setTextColor(34, 197, 94);
    doc.text(`${summary.drivingDays} dia(s) viagem`, margin + 112, y + 5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Total paragens: ${fmtMin(summary.totalBreakMin)}`, margin + 4, y + 10);
    y += 16;

    // Daily breakdown
    const dailyRests = stops.filter(s => s.type && s.type.includes('rest'));
    const numDays = dailyRests.length + 1;

    // Table header
    const colX = [margin, margin + 8, margin + 56, margin + 92, margin + 118, margin + 148];
    const colLabels = ['#', 'Tipo de paragem', 'Local', 'Km', 'Duração', 'Hora'];
    doc.setFillColor(30, 64, 175);
    doc.roundedRect(margin, y, contentW, 6, 1, 1, 'F');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    colLabels.forEach((label, i) => {
      doc.text(label, colX[i] + 2, y + 4.2);
    });
    y += 8;

    // Stop type colors
    const stopColors = {
      break_45: [245, 158, 11],
      daily_rest: [139, 92, 246],
      daily_rest_reduced: [168, 85, 247],
      weekly_rest: [239, 68, 68],
    };
    const stopLabels = {
      break_45: 'Pausa obrigatória (45min)',
      daily_rest: 'Descanso diário (11h)',
      daily_rest_reduced: 'Descanso diário reduzido (9h)',
      weekly_rest: 'Descanso semanal (45h)',
    };

    let dayNum = 1;
    let prevDriveMin = 0;

    // Day header
    function drawDayHeader(dayN, driveInDay) {
      let yy = checkPage(doc, y, 12);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, yy, contentW, 6, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text(`DIA ${dayN}`, margin + 3, yy + 4.2);
      if (driveInDay != null) {
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Condução prevista: ${fmtMin(driveInDay)}`, margin + 20, yy + 4.2);
      }
      return yy + 8;
    }

    // Calculate per-day driving
    const dayDrives = [];
    let prevAcc = 0;
    stops.forEach(s => {
      if (s.type && s.type.includes('rest')) {
        dayDrives.push(s.drivingMinBefore - prevAcc);
        prevAcc = s.drivingMinBefore;
      }
    });
    dayDrives.push(durationMin - prevAcc);

    y = drawDayHeader(1, dayDrives[0] || null);

    stops.forEach((stop, i) => {
      y = checkPage(doc, y, 14);
      const color = stopColors[stop.type] || [245, 158, 11];
      const isRest = stop.type && stop.type.includes('rest');

      // Drive segment before this stop
      const segDrive = stop.drivingMinBefore - prevDriveMin;
      if (segDrive > 0) {
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(59, 130, 246);
        doc.text('→', colX[0] + 2, y);
        doc.setTextColor(71, 85, 105);
        doc.text(`Condução: ${fmtMin(segDrive)}`, colX[1] + 2, y);
        y += 5;
      }

      // Colored left accent
      doc.setFillColor(...color);
      doc.rect(margin, y - 3, 1.5, 8, 'F');

      // Row background
      doc.setFillColor(...color, 10);
      doc.roundedRect(margin + 2, y - 3.5, contentW - 2, 8.5, 1, 1, 'F');

      // Stop number
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text(`${i + 1}`, colX[0] + 3, y + 1);

      // Type
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text(stop.label || stopLabels[stop.type] || 'Pausa', colX[1] + 2, y + 1, { maxWidth: 33 });

      // Location
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      const location = stop.suggestedRestArea?.name || stop.location || '—';
      doc.text(location.substring(0, 28), colX[2] + 2, y + 1, { maxWidth: 24 });

      // Km
      doc.setTextColor(15, 23, 42);
      doc.text(`${stop.km}`, colX[3] + 2, y + 1);

      // Duration
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text(fmtMin(stop.duration), colX[4] + 2, y + 1);

      // Time
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      const timeStr = stop.arrivalTime ? (stop.arrivalDate ? `${stop.arrivalDate} ${stop.arrivalTime}` : stop.arrivalTime) : '—';
      doc.text(timeStr, colX[5] + 2, y + 1);

      // Preferred badge
      if (stop.suggestedRestArea?.preferred) {
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(34, 197, 94);
        doc.text('PREFERENCIAL', colX[2] + 2, y + 5);
      }

      y += 8;
      prevDriveMin = stop.drivingMinBefore;

      // After daily rest → new day header
      if (isRest) {
        dayNum++;
        y = checkPage(doc, y, 12);
        y = drawDayHeader(dayNum, dayDrives[dayNum - 1] || null);
      }
    });

    // Final drive segment
    const lastDrive = durationMin - prevDriveMin;
    if (lastDrive > 0) {
      y = checkPage(doc, y, 8);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(59, 130, 246);
      doc.text('→', colX[0] + 2, y);
      doc.setTextColor(71, 85, 105);
      doc.text(`Condução final: ${fmtMin(lastDrive)}`, colX[1] + 2, y);
      doc.setTextColor(34, 197, 94);
      doc.setFont('helvetica', 'bold');
      doc.text('● CHEGADA', colX[2] + 2, y);
      if (etaResult) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(fmtDateShort(etaResult.eta.toISOString()), colX[5] + 2, y);
      }
      y += 8;
    }

    // Regulation footer
    y = checkPage(doc, y, 12);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('Regulamento (CE) n.º 561/2006 — Tempos de condução e repouso para transportes rodoviários', margin + 3, y + 4);
    doc.text('Condução contínua máx: 4h30 | Pausa obrigatória: 45min | Condução diária máx: 9h (10h 2x/semana) | Descanso diário: 11h (9h reduzido)', margin + 3, y + 8);
    y += 14;
  }

  // ═══════════════════════════════════════════════════════════
  // ROAD BREAKDOWN
  // ═══════════════════════════════════════════════════════════
  if (cr?.roadBreakdown) {
    y = checkPage(doc, y, 30);
    y = sectionHeader(doc, y, 'DISTRIBUIÇÃO DE ESTRADAS', margin);
    const rb = cr.roadBreakdown;
    const roadTypes = [
      { key: 'motorway', label: 'Autoestrada', color: [245, 158, 11] },
      { key: 'highway', label: 'Via rápida', color: [139, 92, 246] },
      { key: 'national', label: 'Nacional', color: [239, 68, 68] },
      { key: 'local', label: 'Local', color: [59, 130, 246] },
    ];
    // Bar
    let barX = margin;
    const barH = 6;
    roadTypes.forEach(rt => {
      const pct = rb[rt.key] || 0;
      if (pct > 0) {
        const w = contentW * (pct / 100);
        doc.setFillColor(...rt.color);
        doc.roundedRect(barX, y, w, barH, 1, 1, 'F');
        barX += w;
      }
    });
    y += barH + 4;
    // Legend
    roadTypes.forEach(rt => {
      const pct = rb[rt.key];
      if (!pct) return;
      doc.setFillColor(...rt.color);
      doc.circle(margin + 2, y - 0.5, 1.5, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      const kmForType = distKm * (pct / 100);
      doc.text(`${rt.label}: ${pct.toFixed(0)}% (${kmForType.toFixed(0)} km)`, margin + 6, y);
      y += 5;
    });
    y += 4;
  }

  // ═══════════════════════════════════════════════════════════
  // COUNTRY DISTANCES
  // ═══════════════════════════════════════════════════════════
  if (cr?.countryDistances) {
    const countries = Object.entries(cr.countryDistances).filter(([, km]) => km > 0).sort((a, b) => b[1] - a[1]);
    if (countries.length > 1) {
      y = checkPage(doc, y, 15);
      y = sectionHeader(doc, y, 'DISTÂNCIA POR PAÍS', margin);
      const countryNames = { PRT: 'Portugal', ESP: 'Espanha', FRA: 'França', BEL: 'Bélgica', NLD: 'Holanda', LUX: 'Luxemburgo', DEU: 'Alemanha', CHE: 'Suíça', AUT: 'Áustria', ITA: 'Itália', GBR: 'Reino Unido', AGO: 'Angola' };
      countries.forEach(([code, km]) => {
        const name = countryNames[code] || code;
        const pct = distKm > 0 ? (km / distKm * 100).toFixed(0) : 0;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(`${name}: ${km.toFixed(0)} km (${pct}%)`, margin + 4, y);
        y += 5;
      });
      y += 3;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // COSTS SUMMARY (only in full PDF, hidden for driver version)
  // ═══════════════════════════════════════════════════════════
  if (includeCosts) {
    y = checkPage(doc, y, 25);
    y = sectionHeader(doc, y, 'CUSTOS', margin);
    const customCosts = route.customCosts || [];
    const tollCost = route.tollData?.totalCost || 0;
    const totalCosts = totalFuelCost + tollCost + customCosts.reduce((s, c) => s + (c.value || 0), 0);
    const costPerKm = distKm > 0 ? totalCosts / distKm : 0;

    const costRows = [
      { label: 'Combustível', value: `€${totalFuelCost.toFixed(2)}`, detail: `${totalFuel.toFixed(1)}L × €${pricePerUnit.toFixed(3)}/L` },
      tollCost > 0 && { label: 'Portagens', value: `€${tollCost.toFixed(2)}`, detail: '' },
      ...customCosts.map(c => ({ label: c.name || 'Custo extra', value: `€${(c.value || 0).toFixed(2)}`, detail: '' })),
    ].filter(Boolean);

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, y, contentW, (costRows.length + 1) * 6 + 8, 2, 2, 'F');
    y += 4;
    costRows.forEach(row => {
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(row.label, margin + 4, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(row.value, margin + 60, y);
      if (row.detail) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(row.detail, margin + 90, y);
      }
      y += 6;
    });
    // Total line
    drawLine(doc, y - 1, margin + 4, margin + contentW - 4);
    y += 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text('TOTAL', margin + 4, y);
    doc.text(`€${totalCosts.toFixed(2)}`, margin + 60, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`€${costPerKm.toFixed(3)}/km`, margin + 90, y);
    y += 8;
  }

  // ═══════════════════════════════════════════════════════════
  // ROUTE OPTIONS
  // ═══════════════════════════════════════════════════════════
  const flags = [
    opts.avoidTolls && 'Sem portagens',
    opts.avoidFerries && 'Sem ferries',
    opts.avoidMotorways && 'Sem autoestradas',
    opts.avoidTunnels && 'Sem túneis',
    opts.avoidLEZ && 'Evitar zonas emissão',
    opts.avoidUrbanAreas && 'Evitar áreas urbanas',
    route.roundTrip && 'Ida e volta',
    opts.useTachograph !== false && 'Tacógrafo ativo',
  ].filter(Boolean);
  if (flags.length > 0) {
    y = checkPage(doc, y, 12);
    y = sectionHeader(doc, y, 'OPÇÕES DE ROTA', margin);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(flags.join('  ·  '), margin, y);
    y += 8;
  }

  // ═══════════════════════════════════════════════════════════
  // DRIVER ACKNOWLEDGMENT
  // ═══════════════════════════════════════════════════════════
  y = checkPage(doc, y, 35);
  drawLine(doc, y, margin, pageW - margin);
  y += 6;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('DECLARAÇÃO DE ENTREGA AO MOTORISTA', margin, y);
  y += 6;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('Declaro que recebi a presente planificação de rota e que me comprometo a cumprir os tempos', margin, y);
  y += 4;
  doc.text('de condução e repouso previstos no Regulamento (CE) n.º 561/2006.', margin, y);
  y += 10;
  doc.setTextColor(148, 163, 184);
  doc.text('Data: ____/____/________', margin, y);
  doc.text('Assinatura motorista: ____________________________________', margin + 60, y);
  y += 6;
  doc.text(`Motorista: ${driverName || '________________________'}`, margin, y);
  doc.text(`Veículo: ${vehiclePlate || '________________________'}`, margin + 80, y);

  // ═══════════════════════════════════════════════════════════
  // FOOTER (all pages)
  // ═══════════════════════════════════════════════════════════
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 285, pageW, 12, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.line(0, 285, pageW, 285);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('EcooGPS Planner · planeamento.ecoogps.com', margin, 291);
    doc.text(`Página ${p}/${totalPages}`, pageW / 2, 291, { align: 'center' });
    doc.text(`Rota: ${route.dbId || '—'} · ${fmtDate(new Date().toISOString())}`, pageW - margin, 291, { align: 'right' });
  }

  const safeName = (route.name || 'rota').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const prefix = includeCosts ? 'planificacao' : 'rota_motorista';
  const filename = `${prefix}_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
  if (options.__returnBlob) {
    return { url: doc.output('bloburl').toString(), blob: doc.output('blob'), filename };
  }
  doc.save(filename);
}

/**
 * Same as exportRoutePdf but returns a { url, blob, filename } tuple instead of
 * triggering a download. Used by the Android mockup preview dialog.
 */
export function generateRoutePdfBlob(route, vehiclePlate, driverName, options = {}) {
  return exportRoutePdf(route, vehiclePlate, driverName, { ...options, __returnBlob: true });
}
